import { isImageVariable } from '@carboncopyco/editor-utils';
import { InfoIcon } from '@carboncopyco/icons';
import type { IVariable, TextVariable } from '@carboncopyco/types';
import { MessageBox, notify } from '@carboncopyco/ui';
import { useBoolean } from 'ahooks';
import { useState } from 'react';
import { RuleDeletionAlert } from '../../RuleDeletionAlert';
import { RulesContent, type RulesContentListProps } from '../../RulesManager';
import { VariablePreview } from '../../VariablePreview';
import {
  VariableListItem,
  type VariableListItemProps,
} from './VariableListItem';

export interface VariablesListProps
  extends Pick<VariableListItemProps, 'onUseVariable'> {
  onVariableSelect: () => void;
  variables: IVariable[];
  editVariable: (variables: IVariable) => Promise<unknown>;
  deleteVariable: (variables: IVariable) => Promise<unknown>;
  reorderVariables: (moved: IVariable, after: IVariable) => Promise<unknown>;
  isDraggingAllowed?: boolean;
}

export const VariablesList = ({
  onVariableSelect,
  variables,
  reorderVariables,
  deleteVariable,
  editVariable,
  onUseVariable,
  isDraggingAllowed,
}: VariablesListProps) => {
  const [showDeletionAlert, setShowDeletionAlert] = useBoolean();
  const [variableToDelete, setVariableToDelete] = useState<IVariable | null>(
    null
  );
  const onReorderVariables: RulesContentListProps<IVariable>['onOrderChange'] =
    async ({ rules, destinationIndex }) => {
      const moved = rules[destinationIndex].rule;
      const after = rules[destinationIndex + 1]?.rule;
      await reorderVariables(moved, after).catch(() => {
        notify.snackbar({
          type: 'error',
          label: 'Failed to reorder. Please try again',
        });
      });
    };

  return (
    <>
      <RulesContent.List<IVariable>
        draggable={isDraggingAllowed}
        rules={variables.map((variable) => ({
          id: isImageVariable(variable)
            ? variable.variableId
            : (variable as TextVariable).code,
          rule: variable,
        }))}
        onOpenRule={onVariableSelect}
        onOrderChange={onReorderVariables}
        renderItem={({ ruleItem, open, openRule, closeRule }) => (
          <VariableListItem
            onEdit={editVariable}
            onDelete={(rule) => {
              setVariableToDelete(rule);
              setShowDeletionAlert.setTrue();
            }}
            onOpenRule={openRule}
            variables={variables}
            open={open}
            onCloseRule={closeRule}
            variable={ruleItem.rule}
            onUseVariable={onUseVariable}
          />
        )}
      />
      <RuleDeletionAlert
        title="Delete variable"
        open={showDeletionAlert}
        onClose={setShowDeletionAlert.setFalse}
        onErrorMessage="Failed to delete variable"
        onConfirm={async () => {
          await deleteVariable(variableToDelete);
          setShowDeletionAlert.setFalse();
        }}
        rulePreview={<VariablePreview variable={variableToDelete} />}
      >
        {variableToDelete && isImageVariable(variableToDelete) && (
          <MessageBox
            label="By deleting this variable, you will also delete all Find and Replace rules associated with it."
            warn
            border
            leftIcon={<InfoIcon />}
          />
        )}
      </RuleDeletionAlert>
    </>
  );
};
